import { useEffect, useState } from "react";

import { Cpu } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@needl-ai/common/components/ui/select";
import {
  ASKNEO_SERVICE_STORAGE_KEY,
  useFetchAskneoServices,
} from "@needl-ai/common/hooks/useFetchAskneoServices";

import {
  createDisplayModeListener,
  getDisplayMode,
} from "@/utils/toolResponseUtils";

const getStoredServiceId = (): string | null => {
  try {
    return localStorage.getItem(ASKNEO_SERVICE_STORAGE_KEY);
  } catch {
    return null;
  }
};

const AskneoServiceSelector = () => {
  const [isDevMode, setIsDevMode] = useState(getDisplayMode);
  const { data, isLoading } = useFetchAskneoServices(isDevMode);

  const [selectedId, setSelectedId] = useState<string>(() => {
    const stored = getStoredServiceId();
    return stored ?? "";
  });

  useEffect(() => {
    return createDisplayModeListener((val) => {
      if (!val) {
        localStorage.removeItem(ASKNEO_SERVICE_STORAGE_KEY);
        setSelectedId("");
      }
      setIsDevMode(val);
    });
  }, []);

  useEffect(() => {
    if (!selectedId && data?.default) {
      setSelectedId(data.default);
    }
  }, [data, selectedId]);

  if (!isDevMode) return null;

  const handleChange = (value: string) => {
    setSelectedId(value);
    localStorage.setItem(ASKNEO_SERVICE_STORAGE_KEY, value);
  };

  if (isLoading) {
    return <div className="h-7 w-32 animate-pulse rounded-md bg-muted" />;
  }

  if (!data?.backends.length) return null;

  return (
    <Select value={selectedId} onValueChange={handleChange}>
      <SelectTrigger className="h-7 w-auto min-w-[5rem] text-sm px-2 gap-1.5 border-0 bg-gray-100 hover:bg-gray-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 shadow-none focus:outline-none focus:ring-0 font-medium text-gray-600 dark:text-neutral-400 rounded-md transition-colors duration-200">
        <Cpu className="h-4 w-4 shrink-0" />
        <SelectValue placeholder="Backend" />
      </SelectTrigger>
      <SelectContent>
        {data.backends.map((backend) => (
          <SelectItem key={backend.id} value={backend.id} className="text-xs">
            {backend.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default AskneoServiceSelector;
